/* Instagram mosaic — live Mintt Instafeed adapter + staggered reveal.
   The editor-managed tiles stay visible until the installed app has
   rendered real posts. If the app fails, is disconnected, or JavaScript
   is unavailable, that curated gallery remains the storefront fallback.
   Pairs with assets/section-instagram-mosaic.css (.igm namespace). */

(() => {
  const INSTAGRAM_LIVE_ITEM_SELECTOR = '.instafeed-new-layout-item';
  const INSTAGRAM_LIVE_POST_LIMIT = 8;

  class InstagramMosaic extends HTMLElement {
    connectedCallback() {
      this._setupReveal();
      this._setupLiveFeed();
    }

    _setupReveal() {
      if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
      if (!('IntersectionObserver' in window)) return;

      this.classList.add('igm--reveal');

      this._io = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            if (!entry.isIntersecting) return;
            entry.target.classList.add('is-in');
            this._io.unobserve(entry.target);
          });
        },
        { rootMargin: '0px 0px -8% 0px', threshold: 0.1 }
      );

      this._observeTiles(this.querySelectorAll('.igm__tile, .igm__plate'));
    }

    _observeTiles(tiles) {
      if (!this._io) return;

      tiles.forEach((tile) => {
        if (!tile.classList.contains('is-in')) this._io.observe(tile);
      });
    }

    _setupLiveFeed() {
      this._feedRoot = this.querySelector('#insta-feed');
      if (!this._feedRoot) return;

      // A second legacy target would make Mintt's output unpredictable.
      // In that case, leave this section on its curated fallback.
      const feedRoots = document.querySelectorAll('#insta-feed');
      if (feedRoots.length !== 1 || feedRoots[0] !== this._feedRoot) {
        this._feedRoot.setAttribute('aria-busy', 'false');
        this.classList.add('igm--live-unavailable');
        return;
      }

      // Mintt emits this documented event after it has rendered. The scoped
      // observer remains necessary for late loads and app/theme-editor rerenders.
      this._onInstafeedLoaded = () => this._scheduleLiveSync();
      document.addEventListener('instafeedAppLoaded', this._onInstafeedLoaded);

      if ('MutationObserver' in window) {
        this._liveObserver = new MutationObserver(() => this._scheduleLiveSync());
        this._liveObserver.observe(this._feedRoot, { childList: true, subtree: true });
      }

      this._liveFallbackTimer = window.setTimeout(() => {
        if (!this._getLiveItems().length) {
          this._feedRoot.setAttribute('aria-busy', 'false');
          this.classList.add('igm--live-unavailable');
        }
      }, 12000);

      // The app event may have fired before this section's deferred script.
      this._syncLiveFeed();
    }

    _getLiveItems() {
      if (!this._feedRoot) return [];

      return Array.from(this._feedRoot.querySelectorAll(INSTAGRAM_LIVE_ITEM_SELECTOR)).filter(
        (item) => item.querySelector('a[href] img, a[href] video')
      );
    }

    _scheduleLiveSync() {
      window.clearTimeout(this._liveSyncTimer);
      this._liveSyncTimer = window.setTimeout(() => this._syncLiveFeed(), 80);
    }

    _syncLiveFeed() {
      if (!this._feedRoot) return;

      const items = this._getLiveItems();

      if (!items.length) {
        if (this.classList.contains('igm--live-ready')) {
          window.clearTimeout(this._liveMissingTimer);
          this._liveMissingTimer = window.setTimeout(() => {
            if (!this._getLiveItems().length) {
              this.classList.remove('igm--live-ready');
              this.classList.add('igm--live-unavailable');
            }
          }, 750);
        }
        return;
      }

      window.clearTimeout(this._liveFallbackTimer);
      window.clearTimeout(this._liveMissingTimer);

      items.forEach((item, index) => this._decorateLiveItem(item, index));

      const visibleItems = items.slice(0, INSTAGRAM_LIVE_POST_LIMIT);
      this.classList.add('igm--live-ready');
      this.classList.remove('igm--live-unavailable');
      this._feedRoot.setAttribute('aria-busy', 'false');
      this._observeTiles(visibleItems);
    }

    _decorateLiveItem(item, index) {
      const isVisible = index < INSTAGRAM_LIVE_POST_LIMIT;

      item.classList.toggle('igm__tile', isVisible);
      item.classList.toggle('igm__tile--feature', isVisible && index === 0);
      item.classList.toggle('igm__tile--extra', !isVisible);

      if (!isVisible) return;

      item.style.setProperty('--igm-i', index);

      const link = item.querySelector('a[href]');
      const media = link?.querySelector(
        ':scope > img, :scope > video, .instafeed-new-layout-item-container > img, .instafeed-new-layout-item-container > video'
      );

      if (media) media.classList.add('igm__img', 'igm__live-media');
      if (link) link.classList.add('igm__live-link');
    }

    disconnectedCallback() {
      if (this._io) this._io.disconnect();
      if (this._liveObserver) this._liveObserver.disconnect();
      if (this._onInstafeedLoaded) {
        document.removeEventListener('instafeedAppLoaded', this._onInstafeedLoaded);
      }

      window.clearTimeout(this._liveSyncTimer);
      window.clearTimeout(this._liveFallbackTimer);
      window.clearTimeout(this._liveMissingTimer);

      this.classList.remove('igm--reveal', 'igm--live-ready', 'igm--live-unavailable');
    }
  }

  if (!customElements.get('instagram-mosaic')) {
    customElements.define('instagram-mosaic', InstagramMosaic);
  }
})();
